/**
 * Book Promo Form Handler
 * Handles the book promo signup form submission:
 * 1. Validates the form
 * 2. Submits to Brevo CRM via /contact endpoint, with the CSRF token, the
 *    honeypot fields and (when configured) a reCAPTCHA token — /contact
 *    rejects a request without them
 * 3. Opens a new window to /resources/freebies on success
 *
 * Contract:
 * - May be bundled across multiple pages
 * - Must silently no-op if the form is not present
 * - Must be idempotent (no duplicate submit handlers)
 */

import { orgContactPhrase } from '../../utils/orgContact.js';

const FREEBIES_URL = '/resources/freebies?fromBookPromo=true';

function injectRecaptchaScript(siteKey) {
  if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
  script.async = true;
  document.head.appendChild(script);
}

/** Resolves to a token, or null when reCAPTCHA is not configured or did not load. */
async function getRecaptchaToken(siteKey) {
  if (!siteKey) return null;
  if (typeof window.grecaptcha === 'undefined') {
    const loaded = await new Promise((resolve) => {
      const started = Date.now();
      const poll = setInterval(() => {
        if (typeof window.grecaptcha !== 'undefined') { clearInterval(poll); resolve(true); }
        else if (Date.now() - started > 8000) { clearInterval(poll); resolve(false); }
      }, 50);
    });
    if (!loaded) return null;
  }
  try {
    return await new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(siteKey, { action: 'contact_form' }).then(resolve).catch(reject);
      });
    });
  } catch {
    return null;
  }
}

async function fetchFreshCsrfToken() {
  try {
    const res = await fetch('/api/csrf-token', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    return data.csrfToken || null;
  } catch {
    return null;
  }
}

function initBookPromoForm() {
  const formId = 'book-promo-form';
  const form = document.getElementById(formId);

  // Not a book promo page → silently no-op
  if (!form) return;

  // Prevent double-binding (shared bundles / HMR / multiple entries)
  if (form.dataset.bookPromoInitialized === 'true') return;
  form.dataset.bookPromoInitialized = 'true';

  const siteKey = form.dataset.recaptchaSiteKey || '';
  if (siteKey) injectRecaptchaScript(siteKey);

  const clearInlineMessage = () => {
    const existing = form.parentElement
      ? form.parentElement.querySelector('.form-success-message, .form-error-message')
      : null;
    if (existing) existing.remove();
  };

  const showInlineMessage = (type, text) => {
    clearInlineMessage();

    const message = document.createElement('div');
    message.className = type === 'error' ? 'form-error-message' : 'form-success-message';
    message.setAttribute('role', type === 'error' ? 'alert' : 'status');
    message.textContent = text;

    // The old homepage has no styles for these; the redesign styles them in CSS
    if (!form.hasAttribute('data-styled-messages')) {
      message.style.cssText = type === 'error'
        ? 'padding: 15px; margin-top: 15px; background-color: #f8d7da; border: 1px solid #f5c2c7; border-radius: 4px; color: #842029; display: block;'
        : 'padding: 15px; margin-top: 15px; background-color: #d4edda; border: 1px solid #c3e6cb; border-radius: 4px; color: #155724; display: block;';
    }

    if (form.parentNode) {
      form.parentNode.insertBefore(message, form.nextSibling);
    }

    // Success clears itself; an error stays until the next attempt
    if (type !== 'error') {
      setTimeout(() => { message.remove(); }, 8000);
    }
  };

  const postForm = (data, csrfToken) => fetch('/contact', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(csrfToken && { 'x-csrf-token': csrfToken }),
    },
    body: JSON.stringify({ ...data, ...(csrfToken && { _csrf: csrfToken }) }),
  });

  form.addEventListener('submit', async function (e) {
    e.preventDefault();

    clearInlineMessage();

    const formData = new FormData(form);
    const field = (name) => (formData.get(name) || '').toString();

    const data = {
      firstName: field('firstName'),
      lastName: field('lastName'),
      email: field('email'),
      phone: field('phone'),
      message: field('message'),
      website_url: field('website_url'),
      _hp_ts: field('_hp_ts'),
      formId,
      skipEmails: true,
    };

    if (!data.firstName.trim()) {
      showInlineMessage('error', 'Enter your first name.');
      return;
    }

    if (!data.email.trim()) {
      showInlineMessage('error', 'Enter your email address.');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
    }

    // Open the window now, while the click still counts as a user action;
    // browsers block window.open() after an await.
    const freebiesWindow = window.open('', '_blank');

    try {
      if (siteKey) {
        const recaptchaToken = await getRecaptchaToken(siteKey);
        if (!recaptchaToken) {
          throw Object.assign(new Error('recaptcha'), {
            userMessage: `The spam check didn't load. Refresh the page and try again, or reach us directly at ${orgContactPhrase()}.`,
          });
        }
        data.recaptchaToken = recaptchaToken;
      }

      let csrfToken = field('_csrf') || await fetchFreshCsrfToken();
      let response = await postForm(data, csrfToken);

      // A token can go stale between page load and submit; refresh it once
      if (response.status === 403) {
        const fresh = await fetchFreshCsrfToken();
        if (fresh && fresh !== csrfToken) {
          csrfToken = fresh;
          const tokenInput = form.querySelector('input[name="_csrf"]');
          if (tokenInput) tokenInput.value = fresh;
          response = await postForm(data, csrfToken);
        }
      }

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || `HTTP ${response.status}`);
      }

      if (freebiesWindow && !freebiesWindow.closed) {
        freebiesWindow.location.href = FREEBIES_URL;
      }

      if (window.umami && typeof window.umami.track === 'function') window.umami.track('book_promo_submit');

      form.reset();
      showInlineMessage(
        'success',
        freebiesWindow
          ? `Thank you, ${data.firstName}! Your free resources are opening in a new window.`
          : `Thank you, ${data.firstName}! Your free resources are at ${window.location.origin}${FREEBIES_URL.split('?')[0]}.`
      );
    } catch (error) {
      if (freebiesWindow && !freebiesWindow.closed) freebiesWindow.close();
      console.error('[BookPromo] Form submission error:', error.message);
      showInlineMessage(
        'error',
        error.userMessage || `We couldn't send that. Try again, or reach us directly at ${orgContactPhrase()}.`
      );
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  });
}

// Safe DOM-ready boot
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBookPromoForm);
} else {
  initBookPromoForm();
}
